import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BadgeCheck, Building2, Download, ExternalLink, MapPin, Users, Sparkles, Globe, Loader2, Link2, Megaphone, Pin, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { SkeletonList, SkeletonPost } from '../components/Skeleton';
import QrCodeModal from '../components/QrCodeModal';

export default function OrganizationDashboard() {
  const { orgId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attachingProjectId, setAttachingProjectId] = useState('');
  const [availableProjects, setAvailableProjects] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnForm, setShowAnnForm] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/organizations/${orgId}`).then((r) => r.data),
      api.get(`/organizations/${orgId}/announcements`).then((r) => r.data.announcements).catch(() => []),
    ])
      .then(([o, a]) => { setData(o); setAnnouncements(a || []); })
      .catch(() => toast.error('Could not load organization'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [orgId]);

  useEffect(() => {
    api.get('/research').then((r) => setAvailableProjects(r.data.projects || [])).catch(() => {});
  }, []);

  if (loading || !data) return (
    <div className="pb-24 space-y-4 pt-2" data-testid="org-dashboard-loading">
      <div className="h-24 rounded-3xl bg-white/5 animate-pulse" />
      <div className="grid grid-cols-3 gap-2">
        {[0,1,2].map(i => <div key={i} className="h-16 rounded-2xl bg-white/5 animate-pulse" />)}
      </div>
      <SkeletonPost /><SkeletonPost />
    </div>
  );

  const { organization: org, is_member, dashboard, projects = [] } = data;

  const join = async () => {
    try {
      await api.post(`/organizations/${orgId}/join`);
      toast.success('Joined ' + org.name);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not join');
    }
  };

  const attach = async () => {
    if (!attachingProjectId) return;
    try {
      await api.post(`/organizations/${orgId}/attach-project`, { project_id: attachingProjectId });
      toast.success('Project attached.');
      setAttachingProjectId('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not attach project');
    }
  };

  const download = async (format) => {
    try {
      const res = await api.get(`/organizations/${orgId}/export?format=${format}`, { responseType: 'blob' });
      const mime = format === 'csv' ? 'text/csv' : 'application/geo+json';
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${org.name.replace(/\s+/g, '_')}-observations.${format === 'csv' ? 'csv' : 'geojson'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed — attach at least one research project first.');
    }
  };

  return (
    <div className="pb-24 space-y-5" data-testid={`org-dashboard-${org.org_id}`}>
      <div className="flex items-center gap-2 pt-2">
        <button onClick={() => nav('/organizations')} data-testid="org-back-btn" className="p-2 rounded-full hover:bg-white/5">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-accent flex items-center gap-1">
            {org.type.replace('_', ' ')}
            {org.verified && <BadgeCheck className="w-3.5 h-3.5 text-emerald-400" />}
          </p>
          <h1 className="font-serif text-2xl truncate" data-testid="org-name">{org.name}</h1>
          {org.region_hint && <p className="text-[11px] text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {org.region_hint}</p>}
        </div>
        {!is_member && org.verified && (
          <button onClick={join} data-testid="org-join-btn" className="rounded-full bg-emerald-500 text-black text-xs font-semibold px-3 py-1.5 hover:bg-emerald-400">
            Join
          </button>
        )}
        {org.verified && (
          <button
            onClick={() => setShowQr(true)}
            data-testid="org-share-public-btn"
            className="ml-2 rounded-full bg-black/40 border border-emerald-400/25 text-emerald-300 text-[10px] px-3 py-1.5 hover:border-emerald-400/50"
            title="Copy or QR-share the public link"
          >
            Share
          </button>
        )}
      </div>

      {showQr && (
        <QrCodeModal
          url={`${window.location.origin}/orgs/public/${orgId}`}
          title={org.name}
          subtitle="Public organization page"
          onClose={() => setShowQr(false)}
        />
      )}

      <div className="rounded-2xl border border-white/5 bg-card p-4 space-y-2">
        <p className="text-sm text-white/85">{org.description}</p>
        <div className="flex flex-wrap gap-3 pt-1">
          {org.website && (
            <a href={org.website} target="_blank" rel="noreferrer"
               className="text-[11px] text-accent flex items-center gap-1 hover:underline">
              <Globe className="w-3 h-3" /> {org.website.replace(/^https?:\/\//, '')}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {org.contact_email && (
            <a href={`mailto:${org.contact_email}`} className="text-[11px] text-accent flex items-center gap-1 hover:underline">
              <ExternalLink className="w-3 h-3" /> {org.contact_email}
            </a>
          )}
        </div>
        {!org.verified && (
          <div className="mt-2 rounded-lg bg-amber-500/10 border border-amber-400/25 p-2 text-[11px] text-amber-200" data-testid="org-unverified-notice">
            Awaiting verification by WildQuest staff. Dashboard is visible to members only.
          </div>
        )}
      </div>

      {is_member && dashboard && (
        <>
          <AnnouncementsSection
            orgId={orgId}
            announcements={announcements}
            currentUserId={data?.organization?.created_by /* fallback */}
            canPost={is_member}
            showForm={showAnnForm}
            setShowForm={setShowAnnForm}
            onChanged={load}
          />

          <section data-testid="org-stats">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Live dashboard</p>
            <div className="grid grid-cols-3 gap-2">
              <StatTile label="Observations" value={dashboard.observation_count} icon={Sparkles} />
              <StatTile label="Species" value={dashboard.species_count} icon={Building2} />
              <StatTile label="Contributors" value={dashboard.contributor_count} icon={Users} />
            </div>
            {Object.keys(dashboard.by_category || {}).length > 0 && (
              <div className="mt-3 rounded-2xl border border-white/5 bg-card p-3">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">By category</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(dashboard.by_category).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                    <span key={cat} className="rounded-full bg-black/50 border border-white/5 px-2.5 py-1 text-[10px] font-mono">
                      {cat} <span className="text-accent">{count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section data-testid="org-exports">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Export data</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => download('csv')} data-testid="org-export-csv" className="rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 text-xs flex items-center justify-center gap-2">
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
              <button onClick={() => download('geojson')} data-testid="org-export-geojson" className="rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 text-xs flex items-center justify-center gap-2">
                <Download className="w-3.5 h-3.5" /> GeoJSON
              </button>
            </div>
          </section>

          <section data-testid="org-projects">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Attached research projects</p>
            {projects.length === 0 ? (
              <div className="rounded-2xl border border-white/5 bg-card p-4 text-center text-[11px] text-muted-foreground" data-testid="org-projects-empty">
                No projects yet. Attach one below to start collecting data.
              </div>
            ) : (
              <div className="space-y-2">
                {projects.map((p) => (
                  <div key={p.project_id} className="rounded-2xl border border-white/5 bg-card p-3 flex items-center gap-3">
                    <Link2 className="w-4 h-4 text-accent" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{p.name}</p>
                      <p className="text-[10px] text-muted-foreground">{p.observation_count || 0} observations · {p.member_count || 0} members</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {availableProjects.filter((p) => !(org.project_ids || []).includes(p.project_id)).length > 0 && (
              <div className="mt-2 rounded-2xl border border-white/5 bg-card p-3 space-y-2" data-testid="org-attach-project">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Attach one of yours</p>
                <select
                  value={attachingProjectId}
                  onChange={(e) => setAttachingProjectId(e.target.value)}
                  data-testid="org-attach-project-select"
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">— select —</option>
                  {availableProjects
                    .filter((p) => !(org.project_ids || []).includes(p.project_id))
                    .map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                </select>
                <button
                  onClick={attach}
                  disabled={!attachingProjectId}
                  data-testid="org-attach-project-btn"
                  className="w-full rounded-lg bg-accent text-black font-semibold text-xs py-2 hover:bg-accent/80 disabled:opacity-40"
                >
                  Attach project
                </button>
              </div>
            )}
          </section>

          {(dashboard.top_contributors || []).length > 0 && (
            <section data-testid="org-top-contributors">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Top contributors</p>
              <div className="rounded-2xl border border-white/5 bg-card divide-y divide-white/5 overflow-hidden">
                {dashboard.top_contributors.map((c, i) => (
                  <div key={c.user_id} className="flex items-center gap-3 px-3 py-2">
                    <span className="w-5 text-center font-mono text-xs text-muted-foreground">#{i + 1}</span>
                    {c.picture ? (
                      <img src={c.picture} alt="" className="w-7 h-7 rounded-full object-cover border border-white/10" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-white/5 border border-white/10" />
                    )}
                    <span className="flex-1 text-sm truncate">{c.display_name}</span>
                    <span className="font-mono text-xs text-accent">{c.observations}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function StatTile({ label, value, icon: Icon }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-white/5 bg-black/30 p-3 text-center"
    >
      <Icon className="w-4 h-4 text-accent mx-auto mb-1" strokeWidth={1.6} />
      <p className="font-mono text-lg text-white">{value ?? 0}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</p>
    </motion.div>
  );
}

function AnnouncementsSection({ orgId, announcements, canPost, showForm, setShowForm, onChanged }) {
  return (
    <section data-testid="org-announcements">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Announcements</p>
        {canPost && (
          <button
            onClick={() => setShowForm(!showForm)}
            data-testid="org-post-toggle"
            className="text-[10px] uppercase tracking-widest text-accent flex items-center gap-1 hover:text-accent/80"
          >
            <Megaphone className="w-3 h-3" /> {showForm ? 'Close' : 'New post'}
          </button>
        )}
      </div>

      {showForm && <NewAnnouncementForm orgId={orgId} onPosted={() => { setShowForm(false); onChanged(); }} />}

      {announcements.length === 0 ? (
        <div className="rounded-2xl border border-white/5 bg-card p-4 text-center text-[11px] text-muted-foreground" data-testid="org-announcements-empty">
          No announcements yet. First post kicks the newsletter off.
        </div>
      ) : (
        <div className="space-y-2">
          {announcements.map((a) => (
            <AnnouncementCard key={a.announcement_id} ann={a} orgId={orgId} onChanged={onChanged} />
          ))}
        </div>
      )}
    </section>
  );
}

function NewAnnouncementForm({ orgId, onPosted }) {
  const [form, setForm] = useState({ title: '', body: '', pinned: false });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (form.title.length < 3 || form.body.length < 10) {
      toast.error('Title (≥3 chars) and body (≥10 chars) required.');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/organizations/${orgId}/announcements`, form);
      toast.success('Posted.');
      onPosted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not post.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="rounded-2xl border border-accent/30 bg-black/40 p-3 space-y-2 mb-2" data-testid="org-post-form">
      <input
        data-testid="ann-title"
        placeholder="Post title"
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm"
      />
      <textarea
        data-testid="ann-body"
        placeholder="What's new? A grant, a paper, a new dataset…"
        value={form.body}
        onChange={(e) => setForm({ ...form, body: e.target.value })}
        rows={4}
        className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm resize-none"
      />
      <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <input
          type="checkbox"
          data-testid="ann-pinned"
          checked={form.pinned}
          onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
          className="w-3.5 h-3.5"
        />
        Pin this post to the top
      </label>
      <button
        onClick={submit}
        disabled={saving}
        data-testid="ann-submit"
        className="w-full rounded-lg bg-accent text-black font-semibold text-xs py-2 hover:bg-accent/80 disabled:opacity-50"
      >
        {saving ? 'Posting…' : 'Publish announcement'}
      </button>
    </div>
  );
}

function AnnouncementCard({ ann, orgId, onChanged }) {
  const [deleting, setDeleting] = useState(false);
  const [reactions, setReactions] = useState(ann.reactions || {});
  const [reacting, setReacting] = useState(null);
  const del = async () => {
    if (!window.confirm('Delete this announcement?')) return;
    setDeleting(true);
    try {
      await api.delete(`/organizations/${orgId}/announcements/${ann.announcement_id}`);
      toast.success('Deleted.');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete.');
    } finally {
      setDeleting(false);
    }
  };
  const react = async (emoji) => {
    setReacting(emoji);
    try {
      const res = await api.post(`/organizations/${orgId}/announcements/${ann.announcement_id}/react`, { emoji });
      // rebuild reactions from counts (we don't know full user list, only counts + my flag)
      const next = { ...reactions };
      Object.keys(res.data.counts).forEach((e) => { next[e] = new Array(res.data.counts[e]).fill('u'); });
      Object.keys(next).forEach((e) => { if (!res.data.counts[e]) delete next[e]; });
      setReactions(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || 'Could not react.');
    } finally {
      setReacting(null);
    }
  };
  const when = (() => {
    try { return new Date(ann.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return ''; }
  })();
  const allowedEmojis = ['👍', '❤️', '🎉', '🔥', '🦉', '🌱', '🐺'];
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border p-3 ${ann.pinned ? 'border-amber-400/40 bg-amber-500/5' : 'border-white/5 bg-card'}`}
      data-testid={`org-announcement-${ann.announcement_id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="font-serif text-base flex items-center gap-1.5 min-w-0">
          {ann.pinned && <Pin className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
          <span className="truncate">{ann.title}</span>
        </p>
        <button
          onClick={del}
          disabled={deleting}
          data-testid={`ann-delete-${ann.announcement_id}`}
          className="p-1 rounded hover:bg-white/10 text-muted-foreground hover:text-rose-300"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
        {ann.author_name} · {when}
      </p>
      <p className="text-sm text-white/85 mt-2 whitespace-pre-wrap">{ann.body}</p>
      <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`ann-reactions-${ann.announcement_id}`}>
        {allowedEmojis.map((e) => {
          const count = reactions[e]?.length || 0;
          return (
            <button
              key={e}
              onClick={() => react(e)}
              disabled={reacting === e}
              data-testid={`ann-react-${ann.announcement_id}-${e}`}
              className={`h-7 min-w-[36px] px-2 rounded-full text-xs flex items-center gap-1 transition ${
                count > 0
                  ? 'bg-accent/15 border border-accent/40 text-white'
                  : 'bg-black/30 border border-white/10 text-muted-foreground hover:text-white hover:border-white/30'
              }`}
            >
              <span>{e}</span>
              {count > 0 && <span className="font-mono text-[10px]">{count}</span>}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}
